<template>
  <div class="stl-container">
    <BasePaneHeader compact />
    <div class="task-lists">
      <Suspense
        v-if="period === 'current' || period === 'future'"
        key="batch/today"
      >
        <SidebarTaskList
          ref="todayContainerEl"
          :drop-zone-id="sidebarTodayDropZoneId"
          :date="today"
          :task-ids="todayTaskIds"
          :list-key="todayListKey"
          :is-dragging-task="isDraggingTask"
          :is-foot-task-list-active="isFootTaskListActive"
          :is-outside-drop-zone="isOutsideTodayDropZone"
          :is-collapsable="true"
          :title="todayTitle"
          :is-collapsed="sections.today.state.value === 'collapsed'"
          :hide-scheduled-date="true"
          @update:is-collapsed="updateCollapsedState('today', $event)"
          @unselect-items="$emit('unselectItems')"
          @is-active="activateSection('today', $event)"
        />
        <template #fallback>
          <Loader>
            <BaseSkeletonList :size="8" is-compact />
          </Loader>
        </template>
      </Suspense>
      <Suspense
        v-if="period === 'current' || period === 'future'"
        key="batch/week"
      >
        <SidebarTaskList
          ref="weekContainerEl"
          :drop-zone-id="sidebarWeekDropZoneId"
          :week="week"
          :list-key="weekListKey"
          :task-ids="weekTaskIds"
          :is-dragging-task="isDraggingTask"
          :is-foot-task-list-active="isFootTaskListActive"
          :is-outside-drop-zone="isOutsideWeekDropZone"
          :hide-scheduled-date="true"
          :is-collapsable="true"
          :show-add-button="!isPastWeek"
          :title="weekTitle"
          :is-collapsed="sections.week.state.value === 'collapsed'"
          @update:is-collapsed="updateCollapsedState('week', $event)"
          @unselect-items="$emit('unselectItems')"
          @is-active="activateSection('week', $event)"
        />
        <template #fallback>
          <Loader>
            <BaseSkeletonList :size="8" is-compact />
          </Loader>
        </template>
      </Suspense>
      <Suspense
        v-if="
          (period === 'current' || period === 'future') &&
          overdueTaskIds &&
          overdueTaskIds.length > 0
        "
        key="batch/overdue"
      >
        <SidebarTaskList
          ref="overdueContainerEl"
          :drop-zone-id="sidebarOverdueDropZoneId"
          :week="week"
          :task-ids="overdueTaskIds"
          :is-dragging-task="isDraggingTask"
          :is-foot-task-list-active="isFootTaskListActive"
          :is-outside-drop-zone="true"
          :is-collapsable="true"
          :title="'Overdue'"
          :is-overdue="true"
          :is-collapsed="sections.overdue.state.value === 'collapsed'"
          :show-add-button="false"
          @update:is-collapsed="updateCollapsedState('overdue', $event)"
          @is-active="activateSection('overdue', $event)"
          @unselect-items="$emit('unselectItems')"
        />
        <template #fallback>
          <Loader>
            <BaseSkeletonList :size="8" is-compact />
          </Loader>
        </template>
      </Suspense>
      <Suspense
        v-if="
          (period === 'current' || period === 'future') &&
          upcomingTaskIds &&
          upcomingTaskIds.length > 0
        "
        key="batch/upcoming"
      >
        <SidebarTaskList
          ref="upcomingContainerEl"
          :drop-zone-id="sidebarUpcomingDropZoneId"
          :week="week"
          :task-ids="upcomingTaskIds"
          :is-dragging-task="isDraggingTask"
          :is-foot-task-list-active="isFootTaskListActive"
          :is-outside-drop-zone="isOutsideUpcomingDropZone"
          :is-collapsable="true"
          :title="'Upcoming'"
          :is-collapsed="sections.upcoming.state.value === 'collapsed'"
          :show-add-button="false"
          @update:is-collapsed="updateCollapsedState('upcoming', $event)"
          @is-active="activateSection('upcoming', $event)"
          @unselect-items="$emit('unselectItems')"
        />
        <template #fallback>
          <Loader>
            <BaseSkeletonList :size="8" is-compact />
          </Loader>
        </template>
      </Suspense>
      <Suspense
        v-if="
          (period === 'current' || period === 'future') &&
          unplannedTaskIds &&
          unplannedTaskIds.length > 0
        "
        key="batch/unplanned"
      >
        <SidebarTaskList
          ref="unplannedContainerEl"
          :drop-zone-id="sidebarUnplannedDropZoneId"
          :week="week"
          :list-key="unplannedListKey"
          :task-ids="unplannedTaskIds"
          :is-dragging-task="isDraggingTask"
          :is-foot-task-list-active="isFootTaskListActive"
          :is-outside-drop-zone="isOutsideUnplannedDropZone"
          :is-collapsable="true"
          :title="'Unplanned'"
          :is-collapsed="sections.unplanned.state.value === 'collapsed'"
          :show-add-button="false"
          @update:is-collapsed="updateCollapsedState('unplanned', $event)"
          @is-active="activateSection('unplanned', $event)"
          @unselect-items="$emit('unselectItems')"
        />
        <template #fallback>
          <Loader>
            <BaseSkeletonList :size="8" is-compact />
          </Loader>
        </template>
      </Suspense>
    </div>
  </div>
</template>

<script lang="ts">
import { format } from "date-fns";
import { isString } from "lodash-es";
import {
  computed,
  ComputedRef,
  defineComponent,
  PropType,
  Ref,
  ref,
  watch,
} from "vue";

import type { TaskId, Week } from "@ctrl/controller-rpc";

import BasePaneHeader from "@/components/base/BasePaneHeader.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import * as inject from "@/components/inject";
import Loader from "@/components/Loader.vue";
import SidebarTaskList from "@/components/planner/sidebar/SidebarTaskList.vue";
import {
  sidebarOverdueDropZoneId,
  sidebarTodayDropZoneId,
  sidebarUnplannedDropZoneId,
  sidebarUpcomingDropZoneId,
  sidebarWeekDropZoneId,
} from "@/components/planner/types";
import {
  dayKey,
  personalSectionKey,
  taskListKeys,
  weekKey,
} from "@/components/tasks/ordering";
import { getHTMLElement } from "@/components/utils";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { bindAsync, useReleaser } from "@/ref";

type SectionState = "collapsed" | "expanded";
type Period = "current" | "past" | "future";

type AllSections = "today" | "week" | "overdue" | "upcoming" | "unplanned";

type Section = {
  state: Ref<SectionState>;
  el: Ref<InstanceType<typeof SidebarTaskList> | undefined>;
};

export default defineComponent({
  components: {
    BasePaneHeader,
    BaseSkeletonList,
    Loader,
    SidebarTaskList,
  },
  props: {
    isDraggingTask: { type: Boolean, required: true },
    isFootTaskListActive: { type: Boolean, required: true },
    isOutsideTodayDropZone: { type: Boolean, required: true },
    isOutsideWeekDropZone: { type: Boolean, required: true },
    isOutsideUpcomingDropZone: { type: Boolean, required: true },
    isOutsideUnplannedDropZone: { type: Boolean, required: true },
    isLoadingTasks: { type: Boolean, required: true },
    weekTaskIds: {
      type: Array as PropType<TaskId[] | null>,
      required: true,
    },
    overdueTaskIds: {
      type: Array as PropType<TaskId[] | null>,
      required: true,
    },
    upcomingTaskIds: {
      type: Array as PropType<TaskId[] | null>,
      required: true,
    },
    unplannedTaskIds: {
      type: Array as PropType<TaskId[] | null>,
      required: true,
    },
    week: { type: Object as PropType<Week>, required: true },
    isPastWeek: { type: Boolean, required: true },
    period: { type: String as PropType<Period>, required: true },
    localStoragePrefix: { type: String, required: true },
  },
  emits: {
    setupDropZone: (el: HTMLElement, elId: string) =>
      el instanceof HTMLElement && isString(elId),
    unselectItems: () => true,
  },
  async setup(props, { emit }) {
    const profile = inject.profile();
    const controller = inject.controller();
    const today = inject.today();
    const releaser = useReleaser();
    const { syncState } = useBrowserStore(profile.value.user.accountId);

    // HACK: fetch Today's ids via agendaTasks so membership matches Agenda's
    // day list exactly. This is knowingly unoptimized — the sidebar already
    // receives overdue/upcoming/unplanned from the planner batch, and
    // agendaTasks recomputes those same groups just to read `current`. The
    // proper fix is a controller RPC exposing the day list alone; until the
    // backend provides one, this duplicate fetch stays.
    const agendaTasks = await releaser.add(
      bindAsync(today, (d) => controller.agendaTasks(d))
    );
    const todayTaskIds = computed(() => agendaTasks.value?.current ?? []);

    const todayTitle = computed(() => format(today.value, "EEEE"));
    const weekTitle = computed(() => `Week ${props.week.n}`);
    const todayListKey = computed(() =>
      personalSectionKey(profile.value, dayKey(today.value))
    );
    const weekListKey = computed(() =>
      personalSectionKey(profile.value, weekKey(props.week))
    );
    const unplannedListKey = computed(() =>
      personalSectionKey(profile.value, taskListKeys.unplanned)
    );

    const todayState = releaser.add(
      syncState<SectionState>(
        `${props.localStoragePrefix}:panes/left/sections/today:state`,
        "expanded"
      )
    );
    const weekState = releaser.add(
      syncState<SectionState>(
        `${props.localStoragePrefix}:panes/left/sections/week:state`,
        "expanded"
      )
    );
    const overdueState = releaser.add(
      syncState<SectionState>(
        `${props.localStoragePrefix}:panes/left/sections/overdue:state`,
        "collapsed"
      )
    );
    const upcomingState = releaser.add(
      syncState<SectionState>(
        `${props.localStoragePrefix}:panes/left/sections/upcoming:state`,
        "collapsed"
      )
    );
    const unplannedState = releaser.add(
      syncState<SectionState>(
        `${props.localStoragePrefix}:panes/left/sections/unplanned:state`,
        "collapsed"
      )
    );

    const todayContainerEl = ref<InstanceType<typeof SidebarTaskList>>();
    const weekContainerEl = ref<InstanceType<typeof SidebarTaskList>>();
    const overdueContainerEl = ref<InstanceType<typeof SidebarTaskList>>();
    const upcomingContainerEl = ref<InstanceType<typeof SidebarTaskList>>();
    const unplannedContainerEl = ref<InstanceType<typeof SidebarTaskList>>();

    const sections: ComputedRef<Record<AllSections, Section>> = computed(
      () => ({
        today: { state: todayState, el: todayContainerEl },
        week: { state: weekState, el: weekContainerEl },
        overdue: { state: overdueState, el: overdueContainerEl },
        upcoming: { state: upcomingState, el: upcomingContainerEl },
        unplanned: { state: unplannedState, el: unplannedContainerEl },
      })
    );
    const activeSection = ref<AllSections | null>(null);

    const activateSection = (sectionName: AllSections, active: boolean) => {
      if (active) {
        // If we are setting a section to active (at least one task selected),
        // make sure the currently active section is deactivated i.e. all tasks
        // are deselected.
        if (activeSection.value !== sectionName) {
          unselectTask();
        }
        // Set the new active section.
        activeSection.value = sectionName;
      } else {
        // Out of precaution, unselect the task from the given section.
        unselectTask(sectionName);
        // If the given section is the active one, that means there is no more
        // any active section.
        if (activeSection.value === sectionName) {
          activeSection.value = null;
        }
      }
    };

    const updateCollapsedState = (id: AllSections, value: boolean) => {
      const newState = value ? "collapsed" : "expanded";
      sections.value[id].state.value = newState;
      // If the section is collapsing, deactive the section for items
      // to be selected.
      if (newState === "collapsed") {
        activateSection(id, false);
      }
    };

    const unselectTask = (
      sectionName: AllSections | null = activeSection.value
    ) => {
      if (!sectionName) return;
      sections.value[sectionName].el.value?.unselectTask();
    };

    // Setup resumes past an await: no active instance/effect scope anymore,
    // so unmount cleanup and watcher stops go through the releaser.
    releaser.defer(() => {
      todayContainerEl.value = undefined;
      weekContainerEl.value = undefined;
      overdueContainerEl.value = undefined;
      upcomingContainerEl.value = undefined;
      unplannedContainerEl.value = undefined;
    });
    const containerEls = [
      todayContainerEl,
      weekContainerEl,
      overdueContainerEl,
      upcomingContainerEl,
      unplannedContainerEl,
    ];
    for (const containerEl of containerEls) {
      releaser.defer(
        watch(
          containerEl,
          (container) => {
            if (container) {
              const el = getHTMLElement(container);
              emit("setupDropZone", el, el.id);
            }
          },
          { immediate: true }
        )
      );
    }
    return {
      today,
      todayTaskIds,
      todayContainerEl,
      weekContainerEl,
      overdueContainerEl,
      upcomingContainerEl,
      unplannedContainerEl,
      sections,
      todayTitle,
      weekTitle,
      updateCollapsedState,
      activateSection,
      unselectTask,
      sidebarTodayDropZoneId,
      sidebarWeekDropZoneId,
      sidebarOverdueDropZoneId,
      sidebarUpcomingDropZoneId,
      sidebarUnplannedDropZoneId,
      todayListKey,
      weekListKey,
      unplannedListKey,
    };
  },
});
</script>

<style lang="scss" scoped>
.stl-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;

  .task-lists {
    overflow-y: auto;
    height: 100%;
    padding-bottom: 24px;
  }

  .base-skeleton-list {
    margin-top: 18px;
    margin-inline-start: 14px;
  }
}
</style>
